import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Autocomplete,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import FolderIcon from '@mui/icons-material/Folder';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';

// Task type options with colors
const taskTypes = [
  { value: 'Preproductie', label: 'Preproductie', color: '#818cf8' },
  { value: 'Draaidag', label: 'Draaidag', color: '#f59e0b' },
  { value: 'Montage', label: 'Montage', color: '#10b981' },
  { value: 'Animatie', label: 'Animatie', color: '#ec4899' },
  { value: 'Vrije dag', label: 'Vrije dag', color: '#fbbf24' },
  { value: 'Intern', label: 'Intern', color: '#fef3c7' },
  { value: 'Illustratie', label: 'Illustratie', color: '#d1d5db' },
  { value: 'Projectafronding', label: 'Projectafronding', color: '#a5b4fc' },
  { value: 'Archief', label: 'Archief', color: '#374151' },
  { value: 'Reparatie', label: 'Reparatie', color: '#fca5a5' },
  { value: 'Marketing', label: 'Marketing', color: '#6ee7b7' },
];

// Priority options
const priorityOptions = [
  { value: 'Prio 1', label: 'Prio 1 - Belangrijkste taak vandaag', description: 'Deze taak moet vandaag af' },
  { value: 'Prio 2', label: 'Prio 2 - Hoge prioriteit', description: 'Belangrijke taak deze week' },
  { value: 'Prio 3', label: 'Prio 3 - Normale prioriteit', description: 'Reguliere taak' },
  { value: 'Prio 4', label: 'Prio 4 - Lage prioriteit', description: 'Kan wachten' },
  { value: 'Intern', label: 'Intern', description: 'Interne taak' },
  { value: 'Draaidag', label: 'Draaidag', description: 'Draaidag planning' },
];

function CreateTask() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { accounts } = useMsal();
  const userEmail = accounts?.[0]?.username || '';

  // Pre-fill from URL params
  const prefilledProjectId = searchParams.get('project');
  const prefilledDeliverableId = searchParams.get('deliverable');

  // Form state
  const [formData, setFormData] = useState({
    project: null,
    omschrijving: '',
    deliverable: null,
    opmerking: '',
    assignee: null,
    createdBy: null,
    prioriteit: 'Prio 3',
    urenWerk: '',
    type: 'Montage',
    sendEmail: true,
    startDate: '',
    endDate: '',
  });

  // Data state
  const [projects, setProjects] = useState([]);
  const [deliverables, setDeliverables] = useState([]);
  const [personen, setPersonen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Fetch initial data
  useEffect(() => {
    fetchData();
  }, []);

  // Auto-select current user as creator
  useEffect(() => {
    if (personen.length > 0 && userEmail) {
      const currentUser = personen.find(p => 
        p.fields?.Email?.toLowerCase() === userEmail.toLowerCase()
      );
      if (currentUser) {
        setFormData(prev => ({ ...prev, createdBy: currentUser }));
      }
    }
  }, [personen, userEmail]);

  // Fetch deliverables when project changes
  useEffect(() => {
    if (formData.project) {
      fetchDeliverables(formData.project.id);
    } else {
      setDeliverables([]);
    }
  }, [formData.project]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch projects
      const projectsResponse = await authFetch(`${BACKEND_URL}/Api/projects`);
      if (projectsResponse.ok) {
        const data = await projectsResponse.json();
        const projectList = data.projects || data.records || [];
        setProjects(projectList);

        // Pre-select project if provided
        if (prefilledProjectId) {
          const project = projectList.find(p => p.id === prefilledProjectId);
          if (project) {
            setFormData(prev => ({ ...prev, project }));
          }
        }
      }

      // Fetch personen
      const personenResponse = await authFetch(`${BACKEND_URL}/Api/personen`);
      if (personenResponse.ok) {
        const data = await personenResponse.json();
        // API returns { people, records } - use records for Autocomplete (has fields property)
        setPersonen(data.records || data.people || []);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Kon data niet laden');
    } finally {
      setLoading(false);
    }
  };

  const fetchDeliverables = async (projectId) => {
    try {
      const response = await authFetch(`${BACKEND_URL}/Api/project-overview/${projectId}`);
      if (response.ok) {
        const data = await response.json();
        setDeliverables(data.deliverables || []);

        // Pre-select deliverable if provided
        if (prefilledDeliverableId && data.deliverables) {
          const deliverable = data.deliverables.find(d => d.id === prefilledDeliverableId);
          if (deliverable) {
            setFormData(prev => ({ ...prev, deliverable }));
          }
        }
      }
    } catch (err) {
      console.error('Error fetching deliverables:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.omschrijving.trim()) {
      setError('Omschrijving is verplicht');
      return;
    }
    if (!formData.assignee) {
      setError('Selecteer wie de taak moet uitvoeren');
      return;
    }
    if (!formData.createdBy) {
      setError('Selecteer wie de taak heeft aangemaakt');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const taskData = {
        NaamTaak: formData.omschrijving,
        Type: formData.type,
        PrioNr: formData.prioriteit,
        Opmerking: formData.opmerking || undefined,
        UrenWerk: formData.urenWerk ? parseFloat(formData.urenWerk) : undefined,
        Assignee: formData.assignee ? [formData.assignee.id] : undefined,
        Project: formData.project ? [formData.project.id] : undefined,
        Deliverable: formData.deliverable ? [formData.deliverable.id] : undefined,
        StartDateUitvoerder: formData.startDate || undefined,
        EndDateUitvoerder: formData.endDate || undefined,
      };

      const response = await authFetch(`${BACKEND_URL}/Api/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(taskData),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Taak aanmaken mislukt');
      }

      setSuccess(true);
      
      // Redirect after success
      setTimeout(() => {
        if (formData.project) {
          navigate(`/projects/${formData.project.id}`);
        } else {
          navigate('/dashboard');
        }
      }, 1500);

    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const getProjectLabel = (project) => {
    const nr = project.fields?.Projectnummer || '';
    const name = project.fields?.Projectnaam || project.fields?.Projectnr_en_naam || '';
    return nr ? `${nr} - ${name}` : name;
  };

  const getPersonLabel = (person) => {
    return person.fields?.['Volledige naam'] || person.fields?.Naam || 'Onbekend';
  };

  const getDeliverableLabel = (deliverable) => {
    return deliverable.fields?.['Naam van Deliverable'] || deliverable.fields?.Deliverable || 'Deliverable';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Nieuwe taak aanmaken
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Via dit formulier kun je een taak aanmaken voor een project of deliverable.
          </Typography>
        </Box>
      </Box>

      {/* User info */}
      <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f9fafb', border: '1px solid #e5e7eb' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon sx={{ fontSize: 18, color: '#6b7280' }} />
          <Typography variant="body2">{userEmail}</Typography>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Taak succesvol aangemaakt! Je wordt doorgestuurd...
        </Alert>
      )}

      <Paper sx={{ p: 4, border: '1px solid #e5e7eb' }} component="form" onSubmit={handleSubmit}>
        {/* Project Selection */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Project
          </Typography>
          <Autocomplete
            value={formData.project}
            onChange={(_, newValue) => setFormData(prev => ({ ...prev, project: newValue, deliverable: null }))}
            options={projects}
            getOptionLabel={getProjectLabel}
            renderInput={(params) => (
              <TextField {...params} placeholder="Selecteer een project" size="small" />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FolderIcon sx={{ fontSize: 18, color: '#6b7280' }} />
                  {getProjectLabel(option)}
                </Box>
              </li>
            )}
          />
        </Box>

        {/* Omschrijving */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Omschrijving <span style={{ color: '#ef4444' }}>*</span>
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={formData.omschrijving}
            onChange={(e) => setFormData(prev => ({ ...prev, omschrijving: e.target.value }))}
            placeholder="Beschrijf de taak"
            required
          />
        </Box>

        {/* Deliverable */}
        {formData.project && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Deliverable
            </Typography>
            <Autocomplete
              value={formData.deliverable}
              onChange={(_, newValue) => setFormData(prev => ({ ...prev, deliverable: newValue }))}
              options={deliverables}
              getOptionLabel={getDeliverableLabel}
              renderInput={(params) => (
                <TextField {...params} placeholder="Selecteer een deliverable (optioneel)" size="small" />
              )}
            />
          </Box>
        )}

        {/* Opmerking */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Eventueel een opmerking
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            size="small"
            value={formData.opmerking}
            onChange={(e) => setFormData(prev => ({ ...prev, opmerking: e.target.value }))}
            placeholder="Extra informatie over de taak..."
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Assignee */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
            Wie moet deze taak uitvoeren? <span style={{ color: '#ef4444' }}>*</span>
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Overleg altijd met de Planner (Anoek) of een taak wel in de agenda past. Als je dit niet hebt overlegd, kies dan 01. Nog in te plannen.
          </Typography>
          <Autocomplete
            value={formData.assignee}
            onChange={(_, newValue) => setFormData(prev => ({ ...prev, assignee: newValue }))}
            options={personen}
            getOptionLabel={getPersonLabel}
            renderInput={(params) => (
              <TextField {...params} placeholder="Selecteer een persoon" size="small" />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon sx={{ fontSize: 18, color: '#6b7280' }} />
                  {getPersonLabel(option)}
                </Box>
              </li>
            )}
          />
        </Box>

        {/* Created By */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Ik heb deze taak aangemaakt <span style={{ color: '#ef4444' }}>*</span>
          </Typography>
          <Autocomplete
            value={formData.createdBy}
            onChange={(_, newValue) => setFormData(prev => ({ ...prev, createdBy: newValue }))}
            options={personen}
            getOptionLabel={getPersonLabel}
            renderInput={(params) => (
              <TextField {...params} placeholder="Selecteer een persoon" size="small" />
            )}
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Priority */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
            Prioriteit nummer (1-4) / omschrijving (intern/draaidag)
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            We geven het toe, dit is een rare categorisering, maar het helpt wel! Geef aan of het prio heeft. 
            Je kan prio 1 aangeven, dan is dat de belangrijkste taak die uitvoerder moet doen vandaag!
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup
              value={formData.prioriteit}
              onChange={(e) => setFormData(prev => ({ ...prev, prioriteit: e.target.value }))}
            >
              {priorityOptions.map((opt) => (
                <FormControlLabel
                  key={opt.value}
                  value={opt.value}
                  control={<Radio size="small" />}
                  label={
                    <Typography variant="body2">{opt.label}</Typography>
                  }
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Box>

        {/* Estimated Hours */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
            Aantal uur werk (Inschatting)
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Geef hier een inschatting aan van het aantal uren dat het duurt om de taak uit te voeren.
          </Typography>
          <TextField
            type="number"
            size="small"
            value={formData.urenWerk}
            onChange={(e) => setFormData(prev => ({ ...prev, urenWerk: e.target.value }))}
            placeholder="bijv. 4"
            sx={{ width: 150 }}
            inputProps={{ min: 0, step: 0.5 }}
          />
        </Box>

        {/* Dates */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Startdatum
            </Typography>
            <TextField
              type="date"
              size="small"
              fullWidth
              value={formData.startDate}
              onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Einddatum
            </Typography>
            <TextField
              type="date"
              size="small"
              fullWidth
              value={formData.endDate}
              onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Task Type */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Type taak
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup
              value={formData.type}
              onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
            >
              {taskTypes.map((type) => (
                <FormControlLabel
                  key={type.value}
                  value={type.value}
                  control={<Radio size="small" />}
                  label={
                    <Chip
                      label={type.label}
                      size="small"
                      sx={{
                        backgroundColor: type.color,
                        color: ['#fef3c7', '#d1d5db', '#fbbf24'].includes(type.color) ? '#374151' : '#fff',
                        fontWeight: 500,
                      }}
                    />
                  }
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Send Email */}
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.sendEmail}
                onChange={(e) => setFormData(prev => ({ ...prev, sendEmail: e.target.checked }))}
              />
            }
            label={
              <Typography variant="body2" fontWeight={500}>
                Stuur mail naar Assignee en Planners
              </Typography>
            }
          />
        </Box>

        {/* Submit Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            disabled={submitting}
          >
            Annuleren
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
            disabled={submitting}
            sx={{ borderRadius: 2 }}
          >
            {submitting ? 'Aanmaken...' : 'Taak aanmaken'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

export default CreateTask;
